import { AsyncPipe } from '@angular/common';
import { Component, DestroyRef, inject } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormsModule } from '@angular/forms';
import { MatButton } from '@angular/material/button';
import { MatCard, MatCardContent, MatCardHeader, MatCardSubtitle, MatCardTitle } from '@angular/material/card';
import { MatFormField, MatLabel } from '@angular/material/form-field';
import { MatInput } from '@angular/material/input';
import { MatProgressBar } from '@angular/material/progress-bar';
import { MatSnackBar } from '@angular/material/snack-bar';
import { BehaviorSubject, catchError, EMPTY, finalize, map, Observable, shareReplay, switchMap, tap } from 'rxjs';
import { TSP } from '../../core/models';
import { AdminService } from '../../core/services/admin/admin.service';

/** How many labels the preview asks for. Matches the cap the gateway enforces. */
const DEFAULT_PREVIEW_SIZE = 10;

/** The status, plus the two things the page reads off it rather than showing raw. */
export type CompetenceI18nView = TSP.Internal.Ai.CompetenceI18nStatus & {
  coverage: number;
  invariantsHold: boolean;
};

/**
 * Drives the competence i18n pass: translate the taxonomy, then fold the
 * cross-language duplicates together.
 *
 * The page exists because ai-service has no route out of the cluster, so this
 * run — paid, taxonomy-wide, and irreversible at the merge step — could only be
 * started from a shell in a container. Everything here is deliberately
 * step-by-step rather than one "run it" button: each step is a gate, and the
 * status panel is what says whether the previous one actually landed.
 */
@Component({
  selector: 'app-competence-i18n',
  standalone: true,
  imports: [
    AsyncPipe,
    FormsModule,
    MatButton,
    MatCard,
    MatCardContent,
    MatCardHeader,
    MatCardSubtitle,
    MatCardTitle,
    MatFormField,
    MatInput,
    MatLabel,
    MatProgressBar,
  ],
  templateUrl: './competence-i18n.component.html',
  styleUrls: ['./competence-i18n.component.scss'],
})
export class CompetenceI18nComponent {
  private readonly adminService = inject(AdminService);
  private readonly snackBar = inject(MatSnackBar);
  private readonly destroyRef = inject(DestroyRef);

  private readonly refresh$ = new BehaviorSubject<void>(undefined);

  /**
   * The status panel, re-read after every step.
   *
   * `catchError` sits INSIDE the `switchMap`, not after it. Outside, one failed
   * read completes the outer stream for good and the panel silently stops
   * updating for the rest of the session — on a page whose whole job is telling
   * an operator whether it is safe to spend the next paid window.
   *
   * `coverage` and `invariantsHold` are derived here rather than read as getters
   * off a mutable field: they are functions of one status, so they cannot go out
   * of step with the numbers beside them.
   */
  public readonly status$: Observable<CompetenceI18nView> = this.refresh$.pipe(
    switchMap(() =>
      this.adminService.getCompetenceI18nStatus().pipe(
        catchError(() => {
          this.snackBar.open('Не вдалося прочитати стан пасу.', 'OK', { duration: 4000 });

          return EMPTY;
        }),
      ),
    ),
    map((status) => ({
      ...status,
      coverage: status.activeCompetences
        ? Math.round(((status.activeCompetences - status.untranslated) / status.activeCompetences) * 100)
        : 0,
      // Shown as a verified fact rather than as an absence of evidence, which is
      // why the status counts the two invariants rather than assuming them.
      invariantsHold: status.chains === 0 && status.cycles === 0,
    })),
    shareReplay(1),
  );

  public preview: TSP.Internal.Ai.CompetenceTranslationPreview[] = [];
  public busy = false;

  public previewSize = DEFAULT_PREVIEW_SIZE;
  public translateLimit: number | null = null;
  public proposeLimit: number | null = null;
  public curateLimit = 2000;

  public refreshStatus(): void {
    this.refresh$.next();
  }

  /**
   * Reads a handful of answers before anything is paid for at scale.
   *
   * What the pass writes becomes what a recruiter sees, and the batch window
   * runs for hours — a prompt that mangles a term is only cheap to find here.
   */
  public runPreview(): void {
    this.run(this.adminService.previewCompetenceTranslations(this.previewSize), 'Переклади отримано.', (result) => {
      this.preview = result;
    });
  }

  public runTranslation(): void {
    this.run(
      this.adminService.translateCompetences(this.translateLimit ?? undefined),
      'Вікно перекладу відправлено. Далі воно саме ланцюжить наступні.',
    );
  }

  public runProposal(): void {
    this.run(
      this.adminService.proposeCompetenceI18nMerges(this.proposeLimit ?? undefined),
      'Пропозиції записано. Зливає їх курація.',
    );
  }

  public runCuration(): void {
    this.run(this.adminService.curateCompetences(this.curateLimit), 'Курацію запущено.');
  }

  public runRecount(): void {
    this.run(this.adminService.refreshCompetenceProfileCounts(), 'Перерахунок лічильників запущено.');
  }

  /**
   * One step: run it, report it, and re-read the status.
   *
   * `finalize` rather than a `tap` per outcome — it releases the busy flag on
   * success, on failure and on the component being destroyed mid-flight, which
   * is three places one operator would otherwise have to be lucky about.
   *
   * The refresh only fires on `next`, so a step that failed does not redraw the
   * panel as though something had moved.
   */
  private run<T>(request$: Observable<T>, message: string, onNext: (result: T) => void = () => undefined): void {
    this.busy = true;

    request$
      .pipe(
        tap(onNext),
        tap(() => {
          this.snackBar.open(message, 'OK', { duration: 4000 });
          this.refresh$.next();
        }),
        catchError(() => {
          this.snackBar.open('Не вдалося виконати крок.', 'OK', { duration: 5000 });

          return EMPTY;
        }),
        finalize(() => (this.busy = false)),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe();
  }
}
